/* eslint-disable @repo/internal/dom-events/no-unsafe-event-listeners */
/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useMemo } from 'react';

import { css as cssUnbounded } from '@compiled/react';
import debounce from 'lodash/debounce';
import kebabCase from 'lodash/kebabCase';
import { useIntl } from 'react-intl';

import { cssMap, css, jsx } from '@atlaskit/css';
import type { EditorAppearance } from '@atlaskit/editor-common/types';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { ListHeading } from './ListHeading';
import { ListItem } from './ListItem';
import { messages } from './messages';
import type { Suggestion, SuggestionGroup } from './types';

// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage
const suggestionsContainerStylesUnbounded = cssUnbounded({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'li:first-of-type': {
		borderTopLeftRadius: 'inherit',
		borderTopRightRadius: 'inherit',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'li:last-of-type': {
		borderBottomLeftRadius: 'inherit',
		borderBottomRightRadius: 'inherit',
	},
});

const suggestionsContainerStyles = css({
	width: '100%',
	height: '100%',
	marginTop: token('space.0'),
	marginRight: token('space.0'),
	marginBottom: token('space.0'),
	marginLeft: token('space.0'),
	paddingTop: token('space.0'),
	paddingRight: token('space.0'),
	paddingBottom: token('space.0'),
	paddingLeft: token('space.0'),
	listStyle: 'none',
	display: 'inline-block',
	position: 'relative',
});

// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage
const suggestionsHeadingStyles = cssUnbounded({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'&&': {
		marginTop: token('space.0'),
		marginRight: token('space.0'),
		marginBottom: token('space.0'),
		marginLeft: token('space.0'),
		paddingTop: token('space.075'),
		paddingRight: token('space.150'),
		paddingBottom: token('space.075'),
		paddingLeft: token('space.150'),
		font: token('font.body.small'),
		fontweight: token('font.weight.bold'),
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		textTransform: 'uppercase',
		color: token('color.text.subtle'),
	},
});

// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage
const suggestionsGroupStyles = cssUnbounded({
	margin: 0,
	padding: 0,
});

const suggestionsScrollerPostGAStyles = cssMap({
	default: {
		overflowY: 'auto',
		// When user is typing, the suggestions container shouldn't shrink when there is lots of input
		flex: '0 1 auto',
	},
});

const suggestionsContainerTeam25Styles = css({
	position: 'relative',
	width: '100%',
	height: '100%',
	paddingLeft: token('space.0'),
	paddingRight: token('space.0'),
	paddingTop: token('space.0'),
	paddingBottom: token('space.150'),
	listStyle: 'none',
});

export type Props = {
	appearance: EditorAppearance;
	hideGroupHeading?: boolean;
	setSuggestion?: (suggestion: Suggestion) => void;
	suggestions: Suggestion[];
	suggestionsHeading?: string;
	value?: string;
};

export function reduceSuggestionsIntoGroups(
	output: SuggestionGroup[],
	suggestion: Suggestion,
): SuggestionGroup[] {
	let suggestionGroup: string;

	if (typeof suggestion.groupHeading === 'function') {
		suggestionGroup = suggestion.groupHeading();
	} else {
		suggestionGroup = suggestion.groupHeading || '';
	}

	let existingRecordIndex = output.findIndex((s) => s.groupHeading === suggestionGroup);

	// If the group doesn't exist, create it
	if (existingRecordIndex === -1) {
		const suggestionsLength = output.push({
			groupHeading: suggestionGroup,
			suggestions: [],
		});

		existingRecordIndex = suggestionsLength - 1;
	}

	output[existingRecordIndex].suggestions.push(suggestion);

	return output;
}

export const Suggestions = ({
	suggestionsHeading,
	suggestions,
	value,
	setSuggestion,
	hideGroupHeading,
}: Props): JSX.Element | null => {
	const { formatMessage } = useIntl();

	/**
	 * While scrolling with the mouse, the setSuggestion() function can still be
	 * called by the mousemove event in ListItem.
	 *
	 * So while the scrolling is happening, another scrollTo() is started and
	 * conflicts with the current scrolling momentum, causing the experience to
	 * jitter and feel clunky.
	 *
	 * While scrolling with just the mouse, this is the order of events:
	 * - (multiple) scroll
	 * - mousemove
	 * - (multiple) scroll
	 * - mousemove
	 * - scrollend
	 *
	 * However, if the user leaves the cursor over the list while navigating with
	 * the keyboard and it scrolls to ensure items are visible, the order of
	 * events is:
	 * - (multiple) on scroll
	 * - scrollend
	 * - mousemove
	 *
	 * So to avoid the mousemove from triggering a change, we set a flag to
	 * ignore mousemove events until a bit after its over.
	 */
	const ignoreMouseMoveRef = React.useRef<boolean>(false);
	const scrollerRef = React.useRef<HTMLDivElement>(null);

	const smoothSetSuggestion = React.useCallback(
		(suggestion: Suggestion, eventName: 'focus' | 'mousemove') => {
			if (!setSuggestion) {
				return;
			}

			// Ignore mousemove
			if (eventName === 'mousemove' && ignoreMouseMoveRef.current) {
				return;
			}

			setSuggestion(suggestion);
		},
		[setSuggestion],
	);

	React.useEffect(() => {
		if (!scrollerRef.current) {
			return;
		}

		// Debounced handler to restore tracking after scrolling
		const allowMouseTracking = debounce(() => {
			ignoreMouseMoveRef.current = false;
		}, 75);
		const onScrollEvent = () => {
			ignoreMouseMoveRef.current = true;
			allowMouseTracking();
		};

		const scroller = scrollerRef.current;
		scroller.addEventListener('scroll', onScrollEvent);
		scroller.addEventListener('scrollend', onScrollEvent);

		return () => {
			scroller.removeEventListener('scroll', onScrollEvent);
			scroller.removeEventListener('scrollend', onScrollEvent);
		};
	}, [scrollerRef]);

	const renderedGroupSuggestions = useMemo(() => {
		const customLabelMap: { [key: string]: string | undefined } = {
			'tell-ai': formatMessage(messages.tellAILabel),
			'tell-agent': formatMessage(messages.tellAgentLabel),
		};

		const mapSuggestionsCallback = (suggestionGroup: SuggestionGroup) => {
			const groupHeadingId = `list-heading-${kebabCase(suggestionGroup.groupHeading)}`;
			return (
				<React.Fragment
					key={
						isExperimentEnabled('platform_editor_fix_missing_suggestions_key')
							? `group_${suggestionGroup.groupHeading}`
							: undefined
					}
				>
					{suggestionGroup.groupHeading && !hideGroupHeading && (
						<ListHeading id={groupHeadingId} heading={suggestionGroup.groupHeading} />
					)}
					<ul
						aria-labelledby={groupHeadingId}
						data-testid={`suggestion-group-${kebabCase(
							suggestionGroup.groupHeading || 'Atlassian intelligence',
						)}`}
						key={
							isExperimentEnabled('platform_editor_fix_missing_suggestions_key')
								? undefined
								: `group_${suggestionGroup.groupHeading}`
						}
						css={suggestionsGroupStyles}
					>
						{suggestionGroup.suggestions.map((suggestion) => {
							return (
								<ListItem
									key={suggestion.value}
									suggestion={suggestion}
									customLabel={customLabelMap[suggestion.value] || undefined}
									highlighted={suggestion.value === value}
									setSuggestion={smoothSetSuggestion}
									hasGroupHeading={!!suggestionGroup.groupHeading && !hideGroupHeading}
								/>
							);
						})}
					</ul>
				</React.Fragment>
			);
		};

		return (
			(suggestions || [])
				// Group suggestions by heading to make mapping to components easier later on
				.reduce(reduceSuggestionsIntoGroups, [])
				.map(mapSuggestionsCallback)
		);
	}, [formatMessage, smoothSetSuggestion, suggestions, value, hideGroupHeading]);

	if (!renderedGroupSuggestions.length) {
		return null;
	}

	return (
		<Box
			testId="suggestions-container"
			xcss={suggestionsScrollerPostGAStyles.default}
			ref={scrollerRef}
			// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
			style={{
				borderTop: `${token('border.width')} solid ${token('color.border')}`,
			}}
		>
			{/* eslint-disable-next-line @atlaskit/design-system/use-heading -- Ignored via go/DSP-18766 */}
			{suggestionsHeading && <h2 css={suggestionsHeadingStyles}>{suggestionsHeading}</h2>}
			<div
				data-testid="editor-plugin-ai-suggestion-list"
				id="suggestion-list"
				css={[
					fg('ai-smart-button-team-25') && suggestionsContainerTeam25Styles,
					!fg('ai-smart-button-team-25') && suggestionsContainerStyles,
					!fg('ai-smart-button-team-25') && suggestionsContainerStylesUnbounded,
				]}
			>
				{renderedGroupSuggestions}
			</div>
		</Box>
	);
};
